import { useEffect, useMemo, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import { fetcher } from 'thera-swr/fetcher';

import { callApi } from 'services/config';
import { InvoiceStatus, InvoiceApprovalStatus, globalFetcherSettings } from 'lib/constants';
import { IContract, IInvoice, IInvoiceInfo, IUser } from 'types';
import { IMilestone } from 'components/app-components/add-contractor/types';
import { buildPath } from 'utils/api';
import { getTheraInvoice } from 'utils/transformation';
import { usePermissions } from './usePermissions';

interface RawInvoiceItem {
  invoice: IInvoice;
  recipientUserId: string;
  creatorUserId: string;
  voiderUserId: string | null;
  milestone: IMilestone | null;
  contract: IContract | null;
}

interface PaginatedInvoicesResponse {
  invoices: RawInvoiceItem[];
  users: Record<string, IUser>;
  nextCursor: string | null;
}

interface UseAdminInvoiceSWRProps {
  companyID: string;
  status?: InvoiceStatus;
  approvalStatus?: InvoiceApprovalStatus;
  contractorUserId?: string;
  isAdminPoV?: boolean;
  pageLimit?: number;
}

type TransformedInvoiceInfo = IInvoiceInfo & { invoice: ReturnType<typeof getTheraInvoice> };

function transformInvoiceList(list: RawInvoiceItem[], users: Record<string, IUser>): TransformedInvoiceInfo[] {
  return list.map((item) => ({
    invoice: getTheraInvoice(item.invoice),
    recipientUser: users[item.recipientUserId],
    creatorUser: users[item.creatorUserId],
    voiderUser: item.voiderUserId ? users[item.voiderUserId] : undefined,
    milestone: item.milestone,
    contract: item.contract,
  }));
}

export default function useAdminInvoiceSWR({
  companyID,
  status,
  approvalStatus,
  contractorUserId,
  isAdminPoV = true,
  pageLimit,
}: UseAdminInvoiceSWRProps) {
  const path = buildPath(`/companies/${companyID}/invoices`, {
    status: status,
    approvalStatus: approvalStatus,
    ...(contractorUserId && { userId: contractorUserId }),
    ...(pageLimit && { pageLimit }),
  });
  const { isOnlyAPARManager } = usePermissions();

  const shouldFetch = Boolean(companyID && isAdminPoV && !isOnlyAPARManager);

  const { data, error, mutate, isLoading, isValidating } = useSWR<PaginatedInvoicesResponse>(
    () => (shouldFetch ? [path] : false),
    fetcher(process.env.NEXT_PUBLIC_BACKEND_URL + path),
    globalFetcherSettings,
  );

  const invoiceList: RawInvoiceItem[] = useMemo(() => {
    if (!data) return [];
    return data.invoices || [];
  }, [data]);

  const swrUsers: Record<string, IUser> = useMemo(() => {
    return data?.users || {};
  }, [data]);

  const swrNextCursor: string | null = useMemo(() => {
    return data?.nextCursor || null;
  }, [data]);

  const transformedData = useMemo(() => transformInvoiceList(invoiceList, swrUsers), [invoiceList, swrUsers]);

  // Extra pages loaded via cursor (beyond the initial SWR page)
  const [extraInvoices, setExtraInvoices] = useState<TransformedInvoiceInfo[]>([]);
  const [allUsers, setAllUsers] = useState<Record<string, IUser>>({});
  const [currentNextCursor, setCurrentNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Wipe cursor-loaded pages when the query changes (different filter, status, company).
  // Using path (not swrNextCursor) so SWR background revalidation don't nuke extra pages.
  useEffect(() => {
    setExtraInvoices([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // Seed cursor and hasMore from the SWR first-page response.
  // Runs on initial load AND whenever the first-page cursor changes (query change or revalidation).
  useEffect(() => {
    setCurrentNextCursor(swrNextCursor);
    setHasMore(Boolean(swrNextCursor));
    setAllUsers(swrUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swrNextCursor]);

  const handleLoadMore = async (cursor: string) => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);

    const cursorPath = buildPath(`/companies/${companyID}/invoices`, {
      afterCursor: cursor,
      status,
      approvalStatus,
      ...(contractorUserId && { userId: contractorUserId }),
      ...(pageLimit && { pageLimit }),
    });

    try {
      const res: PaginatedInvoicesResponse = await callApi(cursorPath, 'GET', undefined);
      const newNextCursor = res?.nextCursor || null;
      // Merge new users into accumulated map — existing keys (same user) are overwritten
      // with the same reference, so no duplication occurs
      const mergedUsers = { ...allUsers, ...res.users };
      setAllUsers(mergedUsers);
      const newTransformed = transformInvoiceList(res?.invoices || [], mergedUsers);

      setExtraInvoices((prev) => [...prev, ...newTransformed]);
      setCurrentNextCursor(newNextCursor);
      setHasMore(Boolean(newNextCursor));
    } finally {
      setIsLoadingMore(false);
    }
  };

  const allInvoices = useMemo(() => [...transformedData, ...extraInvoices], [transformedData, extraInvoices]);

  return {
    invoices: allInvoices,
    nextCursor: currentNextCursor,
    hasMore,
    handleLoadMore,
    isLoadingMore,
    // Cast through unknown — consumers expect KeyedMutator<IInvoiceInfo[]> but SWR
    // types mutate against the raw response shape; callers only call it with no args
    mutateAdminInvoices: mutate as unknown as KeyedMutator<IInvoiceInfo[]>,
    isPending: shouldFetch && isLoading,
    isValidating: shouldFetch && isValidating,
    error,
  };
}
